/**
 * Thin API client for the External Submission Portal.
 *
 * All portal endpoints live under `/api/portal/...` (Vite proxies `/api` to the Data Import API
 * during dev; in prod the path is wired by the same reverse proxy that fronts the Data Import API).
 *
 * Token handling: every call carries the magic-link token in the URL path (the same way the
 * borrower received it). We never persist the token in localStorage — closing the tab logs you
 * out. This is intentional for the demo; real portal will move to short-lived JWTs after the
 * first sign-in (P1.5 follow-up).
 */

const API_BASE = '/api/portal';

const _request = async (path, options = {}) => {
	const response = await fetch(`${API_BASE}${path}`, {
		credentials: 'include',
		headers: {
			...(options.body ? {'Content-Type': 'application/json'} : {}),
			...(options.headers || {}),
		},
		...options,
	});
	if (!response.ok) {
		let detail = '';
		try {
			const json = await response.json();
			detail = json?.message || JSON.stringify(json);
		} catch {
			detail = await response.text();
		}
		const error = new Error(`${response.status} ${response.statusText}${detail ? ` — ${detail}` : ''}`);
		error.status = response.status;
		throw error;
	}
	if (response.status === 204) return null;
	return response.json();
};

export const fetchSession = token => _request(`/session/${encodeURIComponent(token)}`);

export const requestUploadLink = (token, submissionId) =>
	_request(`/session/${encodeURIComponent(token)}/submissions/${submissionId}/upload-link`, {method: 'POST'});

export const reportUploaded = (token, submissionId, body) =>
	_request(`/session/${encodeURIComponent(token)}/submissions/${submissionId}/uploaded`, {
		method: 'POST',
		body: JSON.stringify(body),
	});

/**
 * Push the actual file bytes at the pre-signed `/files/upload/:uuid` URL the API issued. Note
 * that this URL is on the API origin (it is *not* under /api/portal) so it goes through the
 * `/api` proxy as well. The body is a multipart form with two fields — `metadata` (JSON) and
 * `content` (file binary), to mirror the internal upload contract.
 */
export const uploadFile = async (uploadPath, file, metadata) => {
	const form = new FormData();
	form.append('metadata', JSON.stringify(metadata));
	form.append('content', file, file.name);
	const url = uploadPath.startsWith('/files/') ? `/api${uploadPath}` : uploadPath;
	const response = await fetch(url, {method: 'POST', body: form, credentials: 'include'});
	if (!response.ok) {
		const text = await response.text().catch(() => '');
		const error = new Error(`upload failed: ${response.status} ${response.statusText}${text ? ` — ${text}` : ''}`);
		error.status = response.status;
		throw error;
	}
	return response.json().catch(() => ({}));
};
